import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { LoaderCircle } from "lucide-react";
import { cn } from "@/utils/common.utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow hover:bg-primary-hover active:bg-primary-pressed",
        destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline: "border border-input shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        dashboardPrimary: "!rounded-[8px] bg-primary text-primary-foreground shadow-sm transition-[colors,transform] hover:bg-primary-hover active:bg-primary-pressed active:translate-y-px focus-visible:ring-2 focus-visible:ring-dash-lime focus-visible:ring-offset-2",
        dashboardOutline: "!rounded-[8px] border border-dash-border bg-white text-dash-ink shadow-none hover:bg-dash-searchBg hover:text-dash-ink focus-visible:ring-2 focus-visible:ring-dash-greenDeep focus-visible:ring-offset-2",
        seamless: "border-0 bg-transparent text-current shadow-none hover:bg-transparent hover:text-current",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
        content: "h-auto w-auto whitespace-normal rounded-none p-0",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({
    children,
    className,
    variant,
    size,
    asChild = false,
    disabled = false,
    loading = false,
    ...props
  }, ref) => {
    const Comp = asChild ? Slot : "button";
    const isDisabled = disabled || loading;
    const resolvedSize = variant === "seamless" && size == null ? "content" : size;
    const styles = cn(buttonVariants({ variant, size: resolvedSize }), className);

    return (
      <Comp
        className={styles}
        ref={ref}
        {...props}
        disabled={asChild ? undefined : isDisabled}
        aria-busy={loading || undefined}
        aria-disabled={asChild && isDisabled ? true : undefined}
        data-loading={loading ? "true" : undefined}
      >
        {loading && !asChild ? (
          <>
            <LoaderCircle className="animate-spin" aria-hidden="true" />
            {children}
            <span className="sr-only">Loading</span>
          </>
        ) : children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
